import { Person } from "@/Common/Models/Person/Person";
import { CommunicationStateType, CommunicationsUIStateInbox } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { CommunicationsTab_OptimizedV2State } from "@/Communications/Components/CommunicationsTab_OptimizedV2";
import { MessageTaskEditContainerState } from "@/Communications/Components/MessageTaskEdit/MessageTaskEditContainer";
import { MessageTaskViewContainerState } from "@/Communications/Components/MessageTaskView/MessageTaskViewContainer";
import { RecallEdit_V2State } from "@/Communications/Components/Recalls/RecallEdit_V2";
import { RecallView_V2State } from "@/Communications/Components/Recalls/RecallView_V2";
import { MessageSearch_OptimizedV2State } from "@/Communications/Components/Search/MessageSearch_OptimizedV2";
import { RecallSearch_OptimizedV2State } from "@/Communications/Components/Search/RecallSearch_OptimizedV2";
import { TaskSearch_OptimizedV2State } from "@/Communications/Components/Search/TaskSearch_OptimizedV2";
import { PortalCommunicationInboxFilterSetting, PortalCommunicationSentFilterSetting } from "@/Communications/Models/PortalCommunication/PortalCommunicationFilterSettings";
import { PortalCommunicationTableSetting } from "@/Communications/Models/PortalCommunication/PortalCommunicationTableSetting";
import { CommunicationsStore } from "@/Communications/Stores/CommunicationsStore";

class CommunicationsHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Communications.CommunicationStatesSave, this.communictaionStatesSave);
        this.RegisterOperation(LocalOperations.Communications.SavePrefilledSelectedClinicians, this.savePrefilledSelectedClinicians);
        this.RegisterOperation(LocalOperations.Communications.SetCurrentSharedItemId, this.setCurrentSharedItemId);
        this.RegisterOperation(LocalOperations.Communications.UpdatePortalCommunicationFetchedSharedItemsAndTotalCount, this.updatePortalCommunicationFetchedSharedItemsAndTotalCount);
        this.RegisterOperation(LocalOperations.Communications.RemovePortalCommunicationFetchedSharedItems, this.removePortalCommunicationFetchedSharedItems);
        this.RegisterOperation(LocalOperations.Communications.UpdatePortalCommunicationFilterSetting, this.updatePortalCommunicationFilterSetting);
        this.RegisterOperation(LocalOperations.Communications.SavePortalCommunicationTableSetting, this.savePortalCommunicationTableSetting);
    }

    private communictaionStatesSave(request: LocalEvent, store: CommunicationsStore): void {
        let type = request.Get<CommunicationStateType>("type");

        let state = null;
        switch (type) {
            case CommunicationStateType.Tab:
                state = request.Get<CommunicationsTab_OptimizedV2State>("state");
                store.saveCommunicationsTabState(state);
                break;

            case CommunicationStateType.MessageSearch:
                state = request.Get<MessageSearch_OptimizedV2State>("state");
                store.saveMessageSearchState(state);
                break;

            case CommunicationStateType.TaskSearch:
                state = request.Get<TaskSearch_OptimizedV2State>("state");
                store.saveTaskSearchState(state);
                break;

            case CommunicationStateType.MessageTaskView:
                state = request.Get<MessageTaskViewContainerState>("state");
                store.saveMessageTaskViewContainerState(state);
                break;

            case CommunicationStateType.MessageTaskEdit:
                state = request.Get<MessageTaskEditContainerState>("state");
                store.saveMessageTaskEditContainerState(state);
                break;

            case CommunicationStateType.RecallSearch:
                state = request.Get<RecallSearch_OptimizedV2State>("state");
                store.saveRecallSearchState(state);
                break;

            case CommunicationStateType.RecallEdit:
                state = request.Get<RecallEdit_V2State>("state");
                store.saveRecallEditState(state);
                break;

            case CommunicationStateType.RecallView:
                state = request.Get<RecallView_V2State>("state");
                store.saveRecallViewState(state);
                break;
        }
    }

    private savePrefilledSelectedClinicians(request: LocalEvent): void {
        const type = request.Get<CommunicationStateType>("type");
        const isTask = request.Get<boolean>("isTask");
        const selectedClinicians = request.Get<Person[]>("selectedClinicians");

        switch (type) {
            case CommunicationStateType.RecallEdit:
                sessionStore.prefillRecallSelectedClinicians = selectedClinicians;
                break;

            case CommunicationStateType.MessageTaskEdit:
                if (isTask) {
                    sessionStore.prefillTaskSelectedClinicians = selectedClinicians;
                }
                else {
                    sessionStore.prefillMessageSelectedClinicians = selectedClinicians;
                }
                break;
        }
    }

    private setCurrentSharedItemId(request: LocalEvent, store: CommunicationsStore): void {
        const currentSharedItemId = request.Get<string>("currentSharedItemId");
        store.currentSharedItemId = currentSharedItemId;
    }

    private updatePortalCommunicationFilterSetting(request: LocalEvent): void {
        const filterSetting = request.Get<PortalCommunicationInboxFilterSetting | PortalCommunicationSentFilterSetting>("filterSetting");
        const currentInboxType = request.Get<CommunicationsUIStateInbox>("inboxType");

        if (!filterSetting) {
            return;
        }

        if (
            currentInboxType === CommunicationsUIStateInbox.Sent
            && filterSetting instanceof PortalCommunicationSentFilterSetting
        ) {
            cacheStore.UserSettings.PortalCommunicationSentFilterSetting = PortalCommunicationSentFilterSetting.toResponseModel(filterSetting);
        }
        else if (
            currentInboxType !== CommunicationsUIStateInbox.Sent
            && filterSetting instanceof PortalCommunicationInboxFilterSetting
        ) {
            const responseModel = PortalCommunicationInboxFilterSetting.toResponseModel(filterSetting);

            switch (currentInboxType) {
                case CommunicationsUIStateInbox.Inbox:
                    cacheStore.UserSettings.PortalCommunicationInboxFilterSetting = responseModel;
                    break;
                case CommunicationsUIStateInbox.Archive:
                    cacheStore.UserSettings.PortalCommunicationArchivedFilterSetting = responseModel;
                    break;
                case CommunicationsUIStateInbox.FlaggedForReview:
                    cacheStore.UserSettings.PortalCommunicationFlaggedFilterSetting = responseModel;
                    break;
            }
        }
    }

    private savePortalCommunicationTableSetting(request: LocalEvent, store: CommunicationsStore): void {
        const tableSetting = request.Get<PortalCommunicationTableSetting>("tableSetting");
        const inboxType = request.Get<CommunicationsUIStateInbox>("inboxType");

        if (!tableSetting || !inboxType) {
            return;
        }

        store.portalCommunicationTableSetting[inboxType] = tableSetting;
    }

    private updatePortalCommunicationFetchedSharedItemsAndTotalCount(request: LocalEvent, store: CommunicationsStore): void {
        const sharedItemIds = request.Get<string[]>("sharedItemIds");
        const totalCount = request.Get<number>("totalCount");

        store.fetchedSharedItemIds = sharedItemIds;
        store.portalCommunicationTotalCount = totalCount;
    }

    private removePortalCommunicationFetchedSharedItems(request: LocalEvent, store: CommunicationsStore): void {
        const sharedItemIdsToRemove = request.Get<string[]>("sharedItemIds");
        store.fetchedSharedItemIds = store.fetchedSharedItemIds.filter(p => !sharedItemIdsToRemove.contains(p));
        store.portalCommunicationTotalCount = (store.portalCommunicationTotalCount > 0) ? (store.portalCommunicationTotalCount - 1) : 0;
    }
}

export { CommunicationsHandler };
